'use client';

import { ArrowForwardRounded, MenuRounded } from '@mui/icons-material';
import { FC, Fragment, ReactElement, useState } from 'react';

import { OPEN_BRIEFING, useBriefingMessage } from '_components/Briefing/versions/NormalBriefing/BriefingDialog';
import { NavigationData } from '_components/layout/Layout';
import { Links } from '_types/public-api';

import { ExploreMenu } from './ExploreMenu';
import { MobileMenu } from './MobileMenu/MobileMenu';
import { MobileMenuHeader } from './MobileMenu/MobileMenuHeader';
import { MobileMenuItem } from './MobileMenu/MobileMenuItem';

type Props = {
  className?: string;
  children: ReactElement;
  navigationData: NavigationData;
  findAgenciesMenu: {
    title: string;
    explore: {
      title: string;
      subtitle: string;
    };
    postAProject: {
      title: string;
      subtitle: string;
    };
  };
  exploreMenu: {
    title: string;
  };
  links?: Links;
};

export const MainMenu: FC<Props> = (props) => {
  const { children, findAgenciesMenu, exploreMenu, links, navigationData } = props;
  const [menu, setMenu] = useState<boolean>(false);
  const [exploreMenuOpen, setExploreMenuOpen] = useState<boolean>(false);
  const [showIntercom, setShowIntercom] = useState<boolean>(true);
  // The Explore panel renders the full expertise hierarchy (~580 nodes) client-side,
  // even on desktop where it sits hidden and is never opened. It carries no crawlable
  // link (its categories are collapsed accordions, no anchors until expanded), so defer
  // mounting it until the user first opens it — keeping those nodes out of the idle DOM.
  const [exploreMounted, setExploreMounted] = useState<boolean>(false);

  const { sendMessage } = useBriefingMessage();

  const toggleMenu = () => {
    setMenu((prev) => !prev);
    setExploreMenuOpen(false);
    setShowIntercom((prev) => !prev);

    if (window.Intercom) {
      window.Intercom('update', {
        hide_default_launcher: showIntercom,
      });
    }
  };

  const openExploreMenu = () => {
    if (!exploreMounted) {
      // First open: mount the panel closed, then flip it open on the next frame
      // so the slide-in transition still plays from its off-screen position.
      setExploreMounted(true);
      requestAnimationFrame(() => setExploreMenuOpen(true));
      return;
    }

    setExploreMenuOpen(true);
  };

  return (
    <Fragment>
      <button
        onClick={toggleMenu}
        aria-label="toggle-menu"
        className="bg-neutral-50 layout-column layout-align-center-center text-secondary-500 border-none p-0 m-0"
        id="toggle-menu-btn">
        <MenuRounded className="text-secondary-500" style={{ fontSize: 24 }} />
      </button>
      <MobileMenu opened={menu} toggleMenu={toggleMenu}>
        <Fragment>
          <section id="find-agencies-menu">
            <MobileMenuHeader title={findAgenciesMenu.title} />
            <ul className="list-reset">
              <li onClick={openExploreMenu} tabIndex={0} onKeyDown={() => {}} role="menuitem">
                <MobileMenuItem
                  dataTestid="navbar-explore-agencies"
                  title={findAgenciesMenu.explore.title}
                  subtitle={findAgenciesMenu.explore.subtitle}
                  className="border-bottom border-secondary-300"
                  iconRight={<ArrowForwardRounded className="text-secondary-500" style={{ fontSize: 16 }} />}
                  wrapLi={false}
                />
              </li>
              <li
                onClick={() => {
                  sendMessage(OPEN_BRIEFING, { cta: 'navbar-project', expertiseId: navigationData.expertiseId });
                }}
                tabIndex={0}
                onKeyDown={() => {}}
                role="menuitem">
                <MobileMenuItem
                  dataTestid="navbar-post-a-project"
                  title={findAgenciesMenu.postAProject.title}
                  subtitle={findAgenciesMenu.postAProject.subtitle}
                  className="border-bottom border-secondary-300"
                  wrapLi={false}
                />
              </li>
            </ul>
          </section>
          {children}
        </Fragment>
      </MobileMenu>

      {exploreMounted && (
        <ExploreMenu
          opened={exploreMenuOpen}
          toggleMenu={toggleMenu}
          closeExploreMenu={() => setExploreMenuOpen(false)}
          title={exploreMenu.title}
          links={links}
        />
      )}
    </Fragment>
  );
};
